/**
 * 管理者向けダッシュボード(トップ画面)。2026-09-12 の指示に沿った構成(①〜⑩)を、
 * 2026-09-18 UIデザインシステムのテンプレートA(docs/design-system.md §5-2。D-90)の並びにした:
 *  期間 → データの状態(直近7日) → 最重要KPI(取得数・納品数・原価率・営業利益) → 要対応 → 会社別採算 → 推移 → オペレーター
 *
 * **表示は全て「集計データがある最新日までの確定値」**。BlueBean の CSV は翌朝取り込むため当日の進捗は持たない。
 * 計算は `dashboardData.ts` に集約し、この画面は並べるだけにする。金額は PL と同じ `buildPl` の結果を使う。
 * 2026-09-15 MTG: 期間(日次/週次/月次/任意期間。既定は当月)を選べるようにし、RANK は数値と同じ色、原価率を RANK の右隣に置いた。
 * 期間は URL(?mode=day|week|month|range&date=…&from=…&to=…)に持つ。
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DateField from '@core/components/DateField';
import { useToast } from '@core/components/Toast';
import { IconChevronLeft, IconChevronRight } from '@core/components/icons';
import AlertPanel, { type AlertItem } from '@core/components/ui/AlertPanel';
import { fetchCommuteCandidates } from '@core/import/commuteConfirmRepo';
import { fetchTrainingAlerts } from '@core/training/trainingRepo';
import type { TrainingAlert } from '@core/training/training';
import type { CommuteCandidate } from '@core/import/commuteSettle';
import DataTable, { type DataTableColumn } from '@core/components/ui/DataTable';
import FilterBar from '@core/components/ui/FilterBar';
import MetricCard, { MetricRow } from '@core/components/ui/MetricCard';
import PageHeader from '@core/components/ui/PageHeader';
import Panel from '@core/components/ui/Panel';
import SalesForecastPanel from './SalesForecastPanel';
import { buildCallActuals, type CallActuals } from './callActuals';
import { fetchAppliedCallCharges } from './monthCloseRepo';
import { fetchCallNumbers } from '@core/masters/settingsPayRepo';
import RankBadge, { rankTextClass } from '@core/components/ui/RankBadge';
import SegmentedControl from '@core/components/ui/SegmentedControl';
import StatusBadge, { type StatusKind } from '@core/components/ui/StatusBadge';
import ToneValue from '@core/components/ui/ToneValue';
import { isValidDateStr, todayStr, weekdayLabel } from '@core/lib/date';
import { BUSINESS_LABELS, type BusinessType, type Company, type DailySummary, type DateStr, type Delivery, type MonthTargets } from '@core/lib/types';
import { fetchCompanies } from '@core/masters/companyRepo';
import { formatCount, formatCurrency, formatDecimal, formatDuration, formatPercent, formatPointDiff } from '@core/utils/formatters';
import { addDays, addMonths, daysInMonth, staffMetrics, type StaffMetrics } from './calc';
import { costRateTone } from './colors';
import { apiCallsByKey, buildDashboard, WATCH_EXCLUDE_APPH, WATCH_MAX_APPH_FUYOHIN, type CompanyLine, type DashPeriod, type Flow, type HourLine, type OpLine, type StyleLine, type WarnCounts } from './dashboardData';
import { fetchCallWindows } from '@core/import/bbApiRepo';
import { fetchOperators } from '@core/masters/operatorRepo';
import { fetchMonthTargets, fetchSummariesRange } from './dashboardRepo';
import { fetchDeliveries } from './deliveryRepo';
import { fetchExpenseDaily, fetchExpenseItems, type ExpenseDaily, type ExpenseItem } from './expenseRepo';
import MiniChart from './MiniChart';
import { buildPl } from './PlPage';
import { rangeOf, shiftAnchor } from './usePeriod';

const fmtDate = (d: DateStr) => `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}${weekdayLabel(d)}`;
const shortYen = (v: number) => (Math.abs(v) >= 10000 ? `¥${Math.round(v / 10000).toLocaleString('ja-JP')}万` : formatCurrency(v));

/** データの状態(直近7日)。数字は「その状態の日数」 */
const WARN_ITEMS: { key: keyof WarnCounts; name: string; to: string; link: string; kind: StatusKind; bad: string; ok: string }[] = [
  { key: 'csv', name: 'CSV取込(BlueBean)', to: '/import', link: '取り込む', kind: 'notEntered', bad: '未取込', ok: '取込済み' },
  { key: 'delivery', name: '納品件数', to: '/summary/deliveries', link: '入力する', kind: 'notEntered', bad: '未入力', ok: '入力済み' },
  { key: 'expense', name: '経費', to: '/summary/deliveries', link: '入力する', kind: 'notEntered', bad: '未入力', ok: '入力済み' },
  { key: 'kintai', name: '出勤の勤怠', to: '/summary/staff', link: '確認する', kind: 'unconfirmed', bad: '未確認', ok: '確認済み' },
];

type DashMode = DashPeriod['mode'];
const DASH_MODE_LABELS: Record<DashMode, string> = { day: '日次', week: '週次', month: '月次', range: '任意期間' };
const DASH_MODE_OPTIONS = (Object.keys(DASH_MODE_LABELS) as DashMode[]).map((key) => ({ key, label: DASH_MODE_LABELS[key] }));

/** from〜to に含まれる月(YYYY-MM)の一覧 */
function monthsIn(from: DateStr, to: DateStr): string[] {
  const out: string[] = [];
  for (let m = from.slice(0, 7); m <= to.slice(0, 7); m = addMonths(m, 1)) out.push(m);
  return out;
}

const RANK_ORDER = ['S', 'A', 'B+', 'B-', 'C'];

export default function HomePage() {
  const toast = useToast();
  const today = todayStr();
  const [params, setParams] = useSearchParams();

  // ---- 期間(既定は当月) ----
  const modeParam = params.get('mode');
  const mode: DashMode = modeParam === 'day' || modeParam === 'week' || modeParam === 'month' || modeParam === 'range' ? modeParam : 'month';
  const dateParam = params.get('date');
  const anchor = (dateParam && isValidDateStr(dateParam) ? dateParam : today) as DateStr;
  const fromParam = params.get('from');
  const toParam = params.get('to');
  const period = useMemo<DashPeriod>(() => {
    if (mode !== 'range') {
      const r = rangeOf(mode, anchor);
      return { mode, from: r.from, to: r.to, label: r.label };
    }
    let from = (fromParam && isValidDateStr(fromParam) ? fromParam : `${today.slice(0, 7)}-01`) as DateStr;
    let to = (toParam && isValidDateStr(toParam) ? toParam : today) as DateStr;
    if (from > to) [from, to] = [to, from];
    return { mode, from, to, label: `${from.replaceAll('-', '/')}〜${to.replaceAll('-', '/')}` };
  }, [mode, anchor, fromParam, toParam, today]);
  /** 期間をURLに書く。null のキーは消す(日次/週次/月次に戻したときに任意期間の from/to を残さない) */
  const setPeriodParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v === null) next.delete(k);
      else next.set(k, v);
    }
    setParams(next, { replace: true });
  };
  const month = period.to.slice(0, 7);
  /** 7日平均比のために、期間の前6日も読む */
  const loadFrom = (addDays(period.to, -6) < period.from ? addDays(period.to, -6) : period.from) as DateStr;
  const periodDays = useMemo(() => {
    let n = 0;
    for (let d = period.from; d <= period.to; d = addDays(d, 1) as DateStr) n++;
    return n;
  }, [period]);

  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [targetsByMonth, setTargetsByMonth] = useState<Map<string, MonthTargets>>(new Map());
  const [summaries, setSummaries] = useState<DailySummary[]>([]);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [expenses, setExpenses] = useState<ExpenseDaily>(new Map());
  const [expenseItems, setExpenseItems] = useState<ExpenseItem[]>([]);
  const [warnData, setWarnData] = useState<{ summaries: DailySummary[]; deliveries: Delivery[]; expenses: ExpenseDaily }>({ summaries: [], deliveries: [], expenses: new Map() });
  const [costView, setCostView] = useState<'with' | 'ex'>('with');
  /** 取得数と納品予定数の推移で見る架電内容(2026-10-17) */
  const [apptBiz, setApptBiz] = useState<BusinessType>('kyutoki');
  /** 会社別の通話回数(BlueBean API の記録)。2社以上へ架電した日の取得率を会社ごとに分けるのに使う(2026-10-17) */
  const [apiCalls, setApiCalls] = useState<Map<string, number> | undefined>(undefined);
  /** 交通費の要確認(出勤の実稼働が月100時間以上で、まだ70%を確定していない人。D-128) */
  const [commutePending, setCommutePending] = useState<CommuteCandidate[]>([]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const months = monthsIn(loadFrom, period.to);
    const warnFrom = addDays(today, -6) as DateStr;
    void Promise.all([
      fetchCompanies(),
      Promise.all(months.map((m) => fetchMonthTargets(m))),
      fetchSummariesRange(loadFrom, period.to),
      fetchDeliveries(loadFrom, period.to),
      fetchExpenseDaily(loadFrom, period.to),
      fetchExpenseItems(),
      // 警告バーは期間に関係なく「今日から直近7日」
      Promise.all([fetchSummariesRange(warnFrom, today), fetchDeliveries(warnFrom, today), fetchExpenseDaily(warnFrom, today)]),
    ])
      .then(([c, ts, s, d, e, it, w]) => {
        if (!alive) return;
        setCompanies(c);
        setTargetsByMonth(new Map(ts.map((t, i) => [months[i]!, t])));
        setSummaries(s);
        setDeliveries(d);
        setExpenses(e);
        setExpenseItems(it);
        setWarnData({ summaries: w[0], deliveries: w[1], expenses: w[2] });
      })
      .catch((e: unknown) => {
        if (alive) toast(e instanceof Error ? e.message : '読み込みに失敗しました');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [loadFrom, period.to, today, toast]);

  useEffect(() => {
    let alive = true;
    void Promise.all([fetchCallWindows(period.from, period.to), fetchOperators(), fetchCompanies()])
      .then(([w, ops, cs]) => alive && setApiCalls(apiCallsByKey(w, ops, cs)))
      .catch(() => alive && setApiCalls(undefined));
    return () => {
      alive = false;
    };
  }, [period.from, period.to]);

  useEffect(() => {
    let alive = true;
    void fetchCommuteCandidates(month)
      .then((l) => alive && setCommutePending(l.filter((c) => !c.confirmed)))
      .catch(() => alive && setCommutePending([]));
    return () => {
      alive = false;
    };
  }, [month]);

  /** トレーニング経過の面談の時期(D-197)。読めない(権限ルールの反映前など)ときは出さない */
  const [trainingDue, setTrainingDue] = useState<TrainingAlert[]>([]);
  useEffect(() => {
    let alive = true;
    void fetchTrainingAlerts(today)
      .then((l) => alive && setTrainingDue(l))
      .catch(() => alive && setTrainingDue([]));
    return () => {
      alive = false;
    };
  }, [today]);

  /** 目標は期間の最後の月の値(原価率目標など) */
  const targets = targetsByMonth.get(month) ?? null;
  const companyMap = useMemo(() => new Map(companies.map((c) => [c.id, c])), [companies]);
  const allRows = useMemo(() => {
    if (!targets) return [] as StaffMetrics[];
    // 売上見込の単価の予備値は、その日の月の目標を使う
    return summaries.flatMap((s) => s.staff.map((st) => staffMetrics(st, s, targetsByMonth.get(s.date.slice(0, 7)) ?? targets, companyMap)));
  }, [summaries, targets, targetsByMonth, companyMap]);
  const monthRows = useMemo(() => allRows.filter((r) => r.date >= period.from && r.date <= period.to), [allRows, period]);
  const monthDeliveries = useMemo(() => deliveries.filter((d) => d.date >= period.from && d.date <= period.to), [deliveries, period]);
  /** 期間の日付(通話料の実績を日ごとに足すのに使う) */
  const monthDates = useMemo(() => {
    const out: DateStr[] = [];
    for (let d = period.from; d <= period.to; d = addDays(d, 1)) out.push(d);
    return out;
  }, [period]);
  const monthExpenses = useMemo(() => {
    const m: ExpenseDaily = new Map();
    for (const [d, v] of expenses) if (d >= period.from && d <= period.to) m.set(d, v);
    return m;
  }, [expenses, period]);

  const cols = useMemo(() => {
    const appts = new Map<string, number>();
    for (const r of monthRows) if (r.company_id) appts.set(r.company_id, (appts.get(r.company_id) ?? 0) + r.appointments);
    const del = new Map<string, number>();
    for (const d of monthDeliveries) for (const [k, v] of Object.entries(d.counts ?? {})) del.set(k, (del.get(k) ?? 0) + v);
    return companies.filter((c) => c.status === 'active').filter((c) => (appts.get(c.id) ?? 0) > 0 || (del.get(c.id) ?? 0) > 0).sort((a, b) => a.id.localeCompare(b.id));
  }, [companies, monthRows, monthDeliveries]);

  /** 通話料の実績(PLと同じ。「PLに反映する」を押した月だけ) */
  const [callActuals, setCallActuals] = useState<CallActuals | null>(null);
  useEffect(() => {
    let alive = true;
    void Promise.all([fetchAppliedCallCharges(month), fetchCallNumbers()])
      .then(([calls, nums]) => {
        if (!alive) return;
        setCallActuals(buildCallActuals(calls ? [{ month, calls }] : [], nums.items));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [month]);

  const pl = useMemo(
    () => (targets ? buildPl(monthRows, cols, monthDeliveries, targets, period.to, period.mode === 'month' ? daysInMonth(month) : periodDays, monthExpenses, expenseItems, callActuals, monthDates) : null),
    [targets, monthRows, cols, monthDeliveries, period, month, periodDays, monthExpenses, expenseItems],
  );
  const plByDate = useMemo(() => {
    const m = new Map<DateStr, { salesSubtotal: number; gross: number; appointments: number }>();
    if (!targets) return m;
    for (const s of summaries) {
      if (s.date < period.from || s.date > period.to || s.staff.length === 0) continue;
      const de: ExpenseDaily = new Map();
      const v = expenses.get(s.date);
      if (v) de.set(s.date, v);
      const p = buildPl(allRows.filter((r) => r.date === s.date), cols, deliveries.filter((d) => d.date === s.date), targets, s.date, 1, de, expenseItems, callActuals, [s.date]);
      m.set(s.date, { salesSubtotal: p.salesSubtotal, gross: p.gross, appointments: p.appointments });
    }
    return m;
  }, [targets, summaries, period, allRows, cols, deliveries, expenses, expenseItems]);

  const data = useMemo(
    () => (targets ? buildDashboard({ period, today, allRows, deliveries, targets, companies, pl, plByDate, warn: warnData, apiCalls }) : null),
    [targets, period, today, allRows, deliveries, companies, pl, plByDate, warnData, apiCalls],
  );

  const header = (
    <>
      <PageHeader title="ダッシュボード" meta={data ? (data.asOf ? `${period.label} ・ ${fmtDate(data.asOf)}までの集計` : `${period.label} ・ 集計データなし`) : period.label} />
      {/* ---- 期間(日次/週次/月次/任意期間) ---- */}
      <FilterBar label="対象期間">
        <SegmentedControl
          label="期間の単位"
          options={DASH_MODE_OPTIONS}
          value={mode}
          onChange={(m) => setPeriodParams(m === 'range' ? { mode: m, from: period.from, to: period.to, date: null } : { mode: m, date: period.to < today ? period.to : today, from: null, to: null })}
        />
        {mode === 'range' ? (
          <span className="ui-date-nav">
            <DateField variant="ui" value={period.from} onChange={(d) => setPeriodParams({ from: d })} />
            <span aria-hidden="true">〜</span>
            <DateField variant="ui" value={period.to} onChange={(d) => setPeriodParams({ to: d })} />
          </span>
        ) : (
          <span className="ui-date-nav">
            <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setPeriodParams({ date: shiftAnchor(mode, anchor, -1) })} aria-label="前の期間">
              <IconChevronLeft size={16} />
            </button>
            {mode === 'month' ? (
              <input type="month" className="ui-input" aria-label="月" value={anchor.slice(0, 7)} onChange={(e) => e.target.value && setPeriodParams({ date: `${e.target.value}-01` })} />
            ) : (
              <DateField variant="ui" value={anchor} onChange={(d) => setPeriodParams({ date: d })} />
            )}
            <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setPeriodParams({ date: shiftAnchor(mode, anchor, 1) })} aria-label="次の期間">
              <IconChevronRight size={16} />
            </button>
          </span>
        )}
        <span className="ui-period-label">{period.label}</span>
      </FilterBar>
    </>
  );

  if (loading || !data) {
    return (
      <div className="screen full dash">
        {header}
        <div className="ui-panel">
          <div className="ui-empty">読み込み中…</div>
        </div>
      </div>
    );
  }

  const warnTotal = data.warn.csv + data.warn.delivery + data.warn.expense + data.warn.kintai;
  const cost = costView === 'with' ? data.costWith : data.costEx;
  const isMonth = period.mode === 'month';
  const costTarget = data.costWith.target;
  const opTone = (v: number | null) => costRateTone(v, costTarget) || '';

  const alerts: AlertItem[] = [
    // 交通費の確認(D-128)。確定は日報/サマリで行う
    ...(commutePending.length > 0
      ? [
          {
            id: 'commute',
            severity: 'warning' as const,
            target: `交通費 ${formatCount(commutePending.length, { unit: '名' })}`,
            message: '出勤の実稼働が月100時間を超えました。日報/サマリで確認して70%で確定してください',
            diff: commutePending.map((c) => `${c.name} ${formatDecimal(c.hours, { digits: 1 })}h`).join(' ／ '),
            action: { label: '日報/サマリを開く', to: `/summary/deliveries?month=${month}` },
          },
        ]
      : []),
    ...(trainingDue.length > 0
      ? [
          {
            id: 'training',
            severity: 'warning' as const,
            target: `面談の時期 ${formatCount(new Set(trainingDue.map((a) => a.track.opId)).size, { unit: '名' })}`,
            message: '新人が 15h・30h・60h に届きました。トレーニング経過で面談を記録してください',
            diff: trainingDue.map((a) => `${a.track.name}(${BUSINESS_LABELS[a.track.business]}) ${a.milestone.milestone}h`).join(' ／ '),
            action: { label: 'トレーニング経過を開く', to: '/training' },
          },
        ]
      : []),
    ...data.alerts.map((a) => ({
      id: a.key,
      severity: (a.severity === 'high' ? 'danger' : 'warning') as AlertItem['severity'],
      target: a.target,
      message: a.message,
      diff: a.diff,
      action: { label: a.actionLabel, to: a.to },
    })),
  ];

  const companyColumns: DataTableColumn<CompanyLine>[] = [
    { key: 'name', header: '会社名', width: 160, nowrap: true, sortValue: (c) => c.name, render: (c) => c.name },
    { key: 'appointments', header: '取得数', width: 84, numeric: true, sortValue: (c) => c.appointments, render: (c) => formatCount(c.appointments) },
    { key: 'delivered', header: '納品数', width: 84, numeric: true, sortValue: (c) => c.delivered, render: (c) => formatCount(c.delivered) },
    { key: 'hitRate', header: '取得率', width: 84, numeric: true, sortValue: (c) => c.hitRate, render: (c) => formatPercent(c.hitRate) },
    { key: 'costPerDelivery', header: '件あたり原価', width: 112, numeric: true, sortValue: (c) => c.costPerDelivery, render: (c) => formatCurrency(c.costPerDelivery) },
    { key: 'gross', header: '粗利', width: 120, numeric: true, sortValue: (c) => c.gross, render: (c) => <ToneValue tone={c.gross < 0 ? 'negative' : ''}>{formatCurrency(c.gross)}</ToneValue> },
    { key: 'grossPerHour', header: '粗利/稼働h', width: 104, numeric: true, sortValue: (c) => c.grossPerHour, render: (c) => formatCurrency(c.grossPerHour) },
    { key: 'costRate', header: '原価率', width: 92, numeric: true, sortValue: (c) => c.costRate, render: (c) => <ToneValue tone={opTone(c.costRate)}>{formatPercent(c.costRate)}</ToneValue> },
    {
      key: 'cancelRate',
      header: 'CXL率',
      width: 84,
      numeric: true,
      sortValue: (c) => c.cancelRate,
      render: (c) => <ToneValue tone={c.cancelRate !== null && c.cancelRate >= 5 ? 'warn' : ''}>{formatPercent(c.cancelRate)}</ToneValue>,
    },
    { key: 'stock', header: '未納品', width: 84, numeric: true, sortValue: (c) => c.stock, render: (c) => formatCount(c.stock) },
  ];

  const opColumns = (withDetail: boolean): DataTableColumn<OpLine>[] => [
    { key: 'name', header: '氏名', width: 130, nowrap: true, render: (o) => o.name },
    { key: 'rank', header: 'RANK', width: 72, align: 'center', render: (o) => <RankBadge rank={o.rank} /> },
    // 原価率は RANK の右隣(2026-09-15 MTG)
    { key: 'costRate', header: '原価率', width: 90, numeric: true, render: (o) => <ToneValue tone={opTone(o.costRate)}>{formatPercent(o.costRate)}</ToneValue> },
    { key: 'apph', header: '見込/h', width: 80, numeric: true, cellClassName: (o) => rankTextClass(o.rank), render: (o) => formatDecimal(o.apph) },
    ...(withDetail
      ? ([
          { key: 'vs7d', header: '7日平均比', width: 96, numeric: true, render: (o) => <ToneValue tone={o.vs7d !== null && o.vs7d <= 75 ? 'warn' : ''}>{formatPercent(o.vs7d, { digits: 0 })}</ToneValue> },
          { key: 'after', header: '後処理(平均)', width: 110, numeric: true, render: (o) => formatDuration(o.afterAvg) },
          { key: 'pay', header: '人件費', width: 110, numeric: true, render: (o) => formatCurrency(o.pay) },
          { key: 'reason', header: '理由', width: 220, render: (o) => <span className="ui-wrap">{o.reason}</span> },
        ] as DataTableColumn<OpLine>[])
      : []),
  ];

  const styleColumns: DataTableColumn<StyleLine>[] = [
    { key: 'label', header: '形態', width: 120, render: (s) => s.label },
    { key: 'people', header: '人数', width: 70, numeric: true, render: (s) => formatCount(s.people, { unit: '名' }) },
    { key: 'apph', header: '取得/h', width: 80, numeric: true, render: (s) => formatDecimal(s.apph) },
    { key: 'ppa', header: '取得1件あたり人件費', width: 150, numeric: true, render: (s) => formatCurrency(s.payPerAppointment) },
    { key: 'costRate', header: '原価率', width: 90, numeric: true, render: (s) => <ToneValue tone={opTone(s.costRate)}>{formatPercent(s.costRate)}</ToneValue> },
  ];

  const stockColumns: DataTableColumn<Flow['stock'][number]>[] = [
    { key: 'name', header: '会社', width: 190, render: (s) => s.name },
    { key: 'stock', header: '未納品', width: 90, numeric: true, render: (s) => formatCount(s.stock, { unit: '件' }) },
  ];

  const warnFrom = addDays(today, -6) as DateStr;
  const hourRows: { key: string; label: string; perLabel: string; hours: HourLine[] }[] = [
    { key: 'all', label: '取得数', perLabel: '1人あたり', hours: data.hours },
    { key: 'kyutoki', label: BUSINESS_LABELS.kyutoki, perLabel: `${BUSINESS_LABELS.kyutoki} 1人あたり`, hours: data.hoursBy.kyutoki },
    { key: 'fuyohin', label: BUSINESS_LABELS.fuyohin, perLabel: `${BUSINESS_LABELS.fuyohin} 1人あたり`, hours: data.hoursBy.fuyohin },
  ];

  return (
    <div className="screen full dash">
      {header}

      {/* ---- ① データの状態(期間に関係なく直近7日。CSV未取込・未入力・未確認を区別する) ---- */}
      <section className="data-status" aria-label="データの状態">
        <div className="data-status-head">
          <h2 className="data-status-title">データの状態</h2>
          <StatusBadge kind={warnTotal > 0 ? 'unconfirmed' : 'confirmed'} label={warnTotal > 0 ? '集計未確定' : '入力・確認済み'} />
          <span className="data-status-meta">
            直近7日({fmtDate(warnFrom)}〜{fmtDate(today)})の入力・確認 ／ 集計データ: {data.asOf ? `${fmtDate(data.asOf)}まで` : 'なし'}
          </span>
        </div>
        <ul className="data-status-list">
          {WARN_ITEMS.map((w) => {
            const n = data.warn[w.key];
            return (
              <li key={w.key} className="data-status-item">
                <span className="data-status-name">{w.name}</span>
                {n > 0 ? <StatusBadge kind={w.kind} label={`${w.bad} ${n}日`} /> : <StatusBadge kind="confirmed" label={w.ok} />}
                {n > 0 && (
                  <Link className="ui-link" to={w.to}>
                    {w.link}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      {/* ---- ② 最重要KPI ---- */}
      <div className="kpi-grid">
        <MetricCard
          label="取得数"
          value={formatCount(data.pace.actual)}
          unit="件"
          status={isMonth && data.pace.gap !== null ? data.pace.gap > 0 ? <StatusBadge kind="warning" label="ペース不足" /> : <StatusBadge kind="success" label="ペースどおり" /> : undefined}
          target={data.pace.target !== null ? `目標 ${formatCount(data.pace.target, { unit: '件' })}` : isMonth ? '目標 未設定' : undefined}
          diff={data.pace.target ? `達成率 ${formatPercent((data.pace.actual / data.pace.target) * 100)}` : undefined}
        >
          {isMonth && (
            <>
              <MetricRow label="必要" value={`${formatDecimal(data.pace.needPerDay, { digits: 1 })}件/日`} />
              <MetricRow label="現在" value={`${formatDecimal(data.pace.nowPerDay, { digits: 1 })}件/日`} />
              <MetricRow
                label={data.pace.gap !== null && data.pace.gap > 0 ? '不足' : '前倒し'}
                value={data.pace.gap === null ? '—' : `${formatDecimal(Math.abs(data.pace.gap), { digits: 1 })}件/日`}
                tone={data.pace.gap !== null && data.pace.gap > 0 ? 'warning' : undefined}
              />
            </>
          )}
        </MetricCard>

        {/* 納品率は現行シートに無い指標のため 2026-09-21 発注者指示で削除(D-115)。件数だけを出す */}
        <MetricCard
          label="納品数"
          value={formatCount(pl?.visits ?? null)}
          unit="件"
          target={data.flow.date ? `${fmtDate(data.flow.date)} ${formatCount(data.flow.delivered, { unit: '件' })}` : '納品の入力なし'}
        />

        <MetricCard
          label="原価率"
          actions={
            <SegmentedControl
              label="原価率の範囲"
              size="sm"
              options={[
                { key: 'with', label: '管理者込み' },
                { key: 'ex', label: '管理者抜き' },
              ]}
              value={costView}
              onChange={setCostView}
            />
          }
          value={cost.rate === null ? '—' : formatDecimal(cost.rate, { digits: 1 })}
          unit={cost.rate === null ? undefined : '%'}
          status={cost.rate === null ? <StatusBadge kind="noData" label="判定不可" /> : cost.over !== null && cost.over > 0 ? <StatusBadge kind="danger" label="超過" /> : <StatusBadge kind="info" label="目標内" />}
          target={`目標 ${formatPercent(cost.target)}`}
          diff={cost.over !== null ? `目標比 ${formatPointDiff(cost.over)}` : undefined}
          diffTone={cost.over !== null && cost.over > 0 ? 'danger' : 'neutral'}
          footnote={cost.cut !== null ? `目標まで ${formatCurrency(cost.cut)} の削減が必要` : undefined}
        />

        <MetricCard
          label="営業利益"
          value={formatCurrency(data.money.op)}
          valueTone={data.money.op < 0 ? 'danger' : undefined}
          status={data.money.op < 0 ? <StatusBadge kind="danger" label="赤字" /> : undefined}
          target={isMonth && data.money.opF !== null ? `月末の着地 ${formatCurrency(data.money.opF)}` : undefined}
        >
          <MetricRow label="売上" value={isMonth && data.money.salesF !== null ? `${formatCurrency(data.money.sales)}(着地 ${formatCurrency(data.money.salesF)})` : formatCurrency(data.money.sales)} />
          <MetricRow label="粗利" value={isMonth && data.money.grossF !== null ? `${formatCurrency(data.money.gross)}(着地 ${formatCurrency(data.money.grossF)})` : formatCurrency(data.money.gross)} />
        </MetricCard>
      </div>

      {/* ---- ③ 要対応(対象・発生内容・差分・操作) ---- */}
      <AlertPanel title="要対応" meta={period.label} items={alerts} emptyMessage="目標を下回っている項目はありません" />

      {/* ---- ③-2 売上見込み(2026-09-24 発注者指示。P-68。当月固定で、上の期間には連動しない) ---- */}
      <SalesForecastPanel today={today} companies={companies} targets={targets} />

      {/* ---- ⑤ 会社別採算 ---- */}
      <Panel
        title="会社別の採算"
        meta={period.label}
        note={`取得率 = 取得 ÷ 通話回数。2社以上へ架電した日は、会社ごとの通話回数(日次【集計】の会社別と同じ BlueBean の記録)で分けています${data.callsUnsplit > 0 ? `(記録が無い ${formatCount(data.callsUnsplit)}人日は1社目に寄っています)` : ''}`}
      >
        <DataTable caption="会社別の採算" columns={companyColumns} rows={data.companies} rowKey={(c) => c.company_id} maxHeight="none" emptyMessage="この期間の取得はありません" />
      </Panel>

      {/* ---- ④ 取得 → 納品 ／ ⑧ 時間帯別 ---- */}
      <div className="analysis-grid">
        <Panel title="会社別の未納品在庫" meta={period.label} note="期間の取得 − 納品。マイナスは取得より納品が多い会社です">
          <DataTable caption="会社別の未納品在庫" columns={stockColumns} rows={data.flow.stock} rowKey={(s) => s.company_id} maxHeight="none" emptyMessage="在庫はありません" />
        </Panel>
        <Panel
          title="時間帯別の取得数"
          meta={period.mode === 'day' ? (data.asOf ? fmtDate(data.asOf) : '') : period.label}
          note={`稼働 ${data.peopleIsTotal ? '延べ' : ''}${formatCount(data.people)}名(給湯器 ${formatCount(data.peopleBy.kyutoki)}・不用品 ${formatCount(data.peopleBy.fuyohin)})。1人あたりは、それぞれの${data.peopleIsTotal ? '延べ' : ''}稼働人数で割った値です`}
        >
          <div className="report-scroll is-auto">
            <table className="report-table is-compact">
              <caption className="sr-only">時間帯別の取得数</caption>
              <thead>
                <tr>
                  <th scope="col">時台</th>
                  {data.hours.map((h) => (
                    <th key={h.hour} scope="col" className="is-num">
                      {h.hour}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {hourRows.map((r) => (
                  <Fragment key={r.key}>
                    <tr className={r.key === 'all' ? undefined : 'is-group-start'}>
                      <th scope="row">{r.label}</th>
                      {r.hours.map((h) => (
                        <td key={h.hour} className="is-num">
                          <ToneValue tone={h.appointments === 0 ? 'muted' : ''}>{formatCount(h.appointments)}</ToneValue>
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <th scope="row">{r.perLabel}</th>
                      {r.hours.map((h) => (
                        <td key={h.hour} className="is-num">
                          {formatDecimal(h.perPerson)}
                        </td>
                      ))}
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      {/* ---- ⑨ 推移 ---- */}
      <div className="analysis-grid">
        <Panel
          title="取得数の推移"
          meta={period.label}
          actions={
            <SegmentedControl
              label="架電内容"
              size="sm"
              options={[
                { key: 'kyutoki', label: BUSINESS_LABELS.kyutoki },
                { key: 'fuyohin', label: BUSINESS_LABELS.fuyohin },
              ]}
              value={apptBiz}
              onChange={setApptBiz}
            />
          }
          note="納品予定数は会社マスタの曜日別の納品予定の合計です"
        >
          <MiniChart
            ariaLabel={`${BUSINESS_LABELS[apptBiz]}の取得数と納品予定数の推移`}
            labels={data.apptTrend.labels}
            series={[
              { key: 'ap', label: '取得数', values: data.apptTrend.by[apptBiz].appointments },
              { key: 'plan', label: '納品予定数', values: data.apptTrend.by[apptBiz].plan },
            ]}
            format={(v) => `${formatCount(v)}件`}
          />
        </Panel>
        <Panel title="原価率の推移" meta={`目標 給湯器 ${formatPercent(data.trend.costTargetBy.kyutoki)} ／ 不用品 ${formatPercent(data.trend.costTargetBy.fuyohin)}`} note="管理者を除いた原価率です">
          <MiniChart
            ariaLabel="給湯器と不用品の原価率の推移と目標"
            labels={data.trend.labels}
            series={[
              { key: 'k', label: BUSINESS_LABELS.kyutoki, color: 'var(--chart-1)', values: data.trend.costBy.kyutoki },
              { key: 'f', label: BUSINESS_LABELS.fuyohin, color: 'var(--chart-2)', values: data.trend.costBy.fuyohin },
              { key: 'kt', label: `${BUSINESS_LABELS.kyutoki}の目標`, kind: 'target', color: 'var(--chart-1)', values: data.trend.labels.map(() => data.trend.costTargetBy.kyutoki) },
              { key: 'ft', label: `${BUSINESS_LABELS.fuyohin}の目標`, kind: 'target', color: 'var(--chart-2)', values: data.trend.labels.map(() => data.trend.costTargetBy.fuyohin) },
            ]}
            format={(v) => formatPercent(v, { digits: 0 })}
          />
        </Panel>
      </div>
      <Panel title="売上と粗利の推移" meta={period.label}>
        <MiniChart
          ariaLabel="売上小計と粗利の推移"
          labels={data.trend.labels}
          series={[
            { key: 'sales', label: '売上小計', values: data.trend.sales },
            { key: 'gross', label: '粗利', values: data.trend.gross },
          ]}
          format={shortYen}
          height={200}
        />
      </Panel>

      {/* ---- ⑥ オペレーター ---- */}
      <div className="analysis-grid">
        <Panel title="要確認のオペレーター" meta={`RANK下位・後処理が長い・直近7日平均から落ちている(見込/h 給湯器 ${formatDecimal(WATCH_EXCLUDE_APPH, { digits: 1 })}以上・不用品 ${formatDecimal(WATCH_MAX_APPH_FUYOHIN, { digits: 1 })}超の人は除く)`}>
          <DataTable caption="要確認のオペレーター" columns={opColumns(true)} rows={data.watchOps} rowKey={(o) => o.op_id} maxHeight="none" emptyMessage="該当はありません" />
        </Panel>
        <Panel title="上位5名" meta={period.label}>
          <DataTable caption="上位5名" columns={opColumns(false)} rows={data.topOps} rowKey={(o) => o.op_id} maxHeight="none" emptyMessage="該当はありません" />
        </Panel>
      </div>

      {/* ---- ⑦ 雇用形態別 ---- */}
      <div className="analysis-grid">
        <Panel title="雇用形態別の採算" meta={period.label}>
          <DataTable caption="雇用形態別の採算" columns={styleColumns} rows={data.styles} rowKey={(s) => s.key} maxHeight="none" />
        </Panel>
        <Panel title="RANKの構成" meta={period.label} note={`B-・C に支払っている人件費 ${formatCurrency(data.lowRankPay)}`}>
          <MiniChart
            type="bar"
            ariaLabel="RANKごとの人数"
            labels={data.ranks.map((r) => r.rank)}
            series={[{ key: 'rank', label: '人数', values: data.ranks.map((r) => r.count) }]}
            barColors={data.ranks.map((r) => `var(--color-grade-${RANK_ORDER.indexOf(r.rank) + 1})`)}
            showValues
            format={(v) => `${formatCount(v)}名`}
            labelEvery={1}
            height={170}
          />
        </Panel>
      </div>

      {/* ---- ⑩ ショートカット ---- */}
      <Panel title="よく使う画面">
        <div className="ui-link-row">
          <Link className="ui-btn ui-btn-secondary" to="/pl?mode=month">
            PL
          </Link>
          <Link className="ui-btn ui-btn-secondary" to="/summary/staff">
            日次【集計】
          </Link>
          <Link className="ui-btn ui-btn-secondary" to="/summary/deliveries">
            日報/サマリ
          </Link>
          <Link className="ui-btn ui-btn-secondary" to="/summary/companies">
            会社別
          </Link>
          <Link className="ui-btn ui-btn-secondary" to="/staff">
            本人成績
          </Link>
          <Link className="ui-btn ui-btn-secondary" to="/import">
            CSVインポート
          </Link>
        </div>
      </Panel>
    </div>
  );
}
